<template>
  <!-- User Permissions Modal -->
  <div
    class="modal fade"
    id="userPermissionsModal"
    tabindex="-1"
    aria-labelledby="userPermissionsModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog pm-dialog">
      <div class="modal-content pm-content">
        <!-- Head -->
        <div class="pm-head">
          <div class="vp-min-w-0">
            <h2 class="pm-title" id="userPermissionsModalLabel">{{ headTitle }}</h2>
            <div class="pm-subtitle">{{ headSubtitle }}</div>
          </div>
          <button
            type="button"
            class="pm-close"
            data-bs-dismiss="modal"
            :aria-label="$t('common.actions.close')"
          >
            <i class="ph-duotone ph-x"></i>
          </button>
        </div>

        <!-- Search and bulk controls -->
        <div v-if="!loading && !error" class="pm-toolbar">
          <label class="pm-search">
            <i class="ph-duotone ph-magnifying-glass"></i>
            <input
              v-model="searchQuery"
              type="search"
              :placeholder="$t('users.permissions.searchPlaceholder')"
              :aria-label="$t('users.permissions.searchPlaceholder')"
            />
          </label>
          <button type="button" class="pm-tool-btn" @click="expandAllGroups">
            <i class="ph-duotone ph-caret-double-down"></i>{{ $t('users.permissions.expandAll') }}
          </button>
          <button type="button" class="pm-tool-btn" @click="collapseAllGroups">
            <i class="ph-duotone ph-caret-double-up"></i>{{ $t('users.permissions.collapseAll') }}
          </button>
          <span class="pm-tool-count">
            {{ $t('users.permissions.enabledCount', { granted: grantedTotal, total: permissionTotal }) }}
          </span>
        </div>

        <!-- Body -->
        <div class="pm-body">
          <!-- Loading State -->
          <div v-if="loading" class="pm-state">
            <span class="spinner-border spinner-border-sm me-2"></span>{{ $t('common.loading') }}
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="pm-state pm-state--error">
            <i class="ph-duotone ph-warning-circle"></i>{{ error }}
          </div>

          <!-- Permission groups -->
          <template v-else-if="visibleGroupNames.length > 0">
            <div
              v-for="groupName in visibleGroupNames"
              :key="groupName"
              class="pm-group"
              :class="{ 'is-on': groupGranted(groupName) > 0 }"
              draggable="true"
              @dragstart="onDragStart($event, groupName)"
              @dragend="onDragEnd($event)"
              @dragover="onDragOver($event)"
              @dragenter="onDragEnter($event, groupName)"
              @dragleave="onDragLeave($event)"
              @drop="onDrop($event, groupName)"
            >
              <div class="pm-group-head">
                <button
                  type="button"
                  class="pm-caret"
                  :aria-expanded="isExpanded(groupName)"
                  :aria-label="$t('users.permissions.expandAll')"
                  @click="toggleGroup(groupName)"
                >
                  <i class="ph-duotone" :class="isExpanded(groupName) ? 'ph-caret-down' : 'ph-caret-right'"></i>
                </button>

                <span class="pm-group-label">{{ getGroupDisplayName(groupName) }}</span>

                <span class="pm-group-count" :class="{ 'is-on': groupGranted(groupName) > 0 }">
                  {{ groupGranted(groupName) }} / {{ groupTotal(groupName) }}
                </span>

                <button
                  type="button"
                  class="pm-group-toggle"
                  :disabled="updating"
                  :aria-pressed="groupState(groupName) === 'all'"
                  @click="toggleGroupAll(groupName)"
                >
                  <span class="pm-switch" :class="`is-${groupState(groupName)}`"><i></i></span>
                  <span class="pm-group-toggle-label">{{ groupStateLabel(groupName) }}</span>
                </button>
              </div>

              <div v-if="isExpanded(groupName)" class="pm-items">
                <button
                  v-for="permission in filteredGroupedPermissions[groupName]"
                  :key="permission.name"
                  type="button"
                  class="pm-item"
                  :class="{ 'is-locked': isLocked(permission) }"
                  :disabled="updating || isLocked(permission)"
                  :aria-pressed="!!permission.isGranted"
                  @click="togglePermissionRow(permission)"
                >
                  <span class="pm-item-text">
                    <span class="pm-item-label">{{ itemLabel(permission) }}</span>
                    <span v-if="isLocked(permission)" class="pm-item-hint">
                      <i class="ph-duotone ph-lock-simple"></i>{{ $t('users.permissions.locked') }}
                    </span>
                  </span>
                  <span class="pm-switch" :class="permission.isGranted ? 'is-all' : 'is-off'"><i></i></span>
                </button>
              </div>
            </div>
          </template>

          <!-- Empty State -->
          <div v-else class="pm-empty">
            <i class="ph-duotone ph-shield-warning"></i>
            <div class="pm-empty-title">{{ $t('users.permissions.noPermissions') }}</div>
            <p class="pm-empty-text">{{ $t('users.permissions.noPermissionsDescription') }}</p>
          </div>
        </div>

        <!-- Foot -->
        <div class="pm-foot">
          <span class="pm-foot-note">{{ $t('users.permissions.autoSaveNote') }}</span>
          <button type="button" class="pm-btn" data-bs-dismiss="modal">
            {{ $t('common.actions.close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// 1. Vue core
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

// 2. Third-party
import Swal from 'sweetalert2';

// 3. Services
import userPermissionService from '@/services/userPermissionService';
import userService from '@/services/userService';
import superAdminService from '@/services/superAdminService';

const { t } = useI18n();
const store = useStore();

const props = defineProps({
  userId: {
    type: String,
    default: null
  },
  show: {
    type: Boolean,
    default: false
  },
  isSuperAdmin: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close', 'error']);

const loading = ref(false);
const updating = ref(false);
const error = ref('');
const permissions = ref([]);
const user = ref(null);
/** Keyed by group name, not index: search filtering reorders the list. */
const expandedGroups = ref({});
const searchQuery = ref('');
const groupOrder = ref([]);

// Load group order from localStorage
const loadGroupOrder = () => {
  const saved = localStorage.getItem('permissionGroupOrder');
  if (saved) {
    groupOrder.value = JSON.parse(saved);
  }
};

// Save group order to localStorage
const saveGroupOrder = () => {
  localStorage.setItem('permissionGroupOrder', JSON.stringify(groupOrder.value));
};

// Helper: Create groups for parent permissions
const createParentGroups = (groups) => {
  const parentPermissions = permissions.value.filter(p => !p.parentName);
  parentPermissions.forEach(parent => {
    groups[parent.name] = [parent];
  });
  return groups;
};

// Helper: Add child permissions to their parent groups
const addChildPermissionsToGroups = (groups) => {
  permissions.value.forEach(permission => {
    if (!permission.parentName) return;

    const parentPermission = permissions.value.find(p => p.name === permission.parentName);
    if (!parentPermission) return;

    if (!groups[permission.parentName]) {
      groups[permission.parentName] = [];
    }
    groups[permission.parentName].push(permission);
  });
  return groups;
};

// Helper: Handle orphaned permissions
const handleOrphanedPermissions = (groups) => {
  permissions.value.forEach(permission => {
    if (!permission.parentName) return;

    const parentExists = permissions.value.some(p => p.name === permission.parentName);
    if (parentExists) return;

    if (!groups['System']) {
      groups['System'] = [];
    }
    groups['System'].push(permission);
  });
  return groups;
};

// Helper: Apply custom order to groups
const applyCustomOrder = (groups) => {
  if (groupOrder.value.length === 0) {
    return groups;
  }

  const orderedGroups = {};

  // Add groups in saved order
  groupOrder.value.forEach(groupName => {
    if (groups[groupName]) {
      orderedGroups[groupName] = groups[groupName];
    }
  });

  // Add any new groups that weren't in saved order
  Object.keys(groups).forEach(groupName => {
    if (!orderedGroups[groupName]) {
      orderedGroups[groupName] = groups[groupName];
    }
  });

  return orderedGroups;
};

// Group permissions by parentName with custom order
const groupedPermissions = computed(() => {
  const groups = {};

  createParentGroups(groups);
  addChildPermissionsToGroups(groups);
  handleOrphanedPermissions(groups);

  return applyCustomOrder(groups);
});

// Get parent permissions (those with no parentName)
const getParentPermission = (groupName) => {
  return permissions.value.find(p => p.name === groupName && !p.parentName);
};

// Check if a permission's parent is granted
const isParentGranted = (permissionName) => {
  const permission = permissions.value.find(p => p.name === permissionName);
  if (!permission?.parentName) return true; // If no parent, it's allowed

  const parent = permissions.value.find(p => p.name === permission.parentName);
  return parent ? parent.isGranted : false;
};

// Check if a permission can be granted (parent dependency check)
const canGrantPermission = (permission) => {
  // If no parentName, it's a top-level permission that can be toggled
  if (!permission.parentName) return true;

  // Check if parent is granted
  return isParentGranted(permission.name);
};

/** A child whose area permission is off cannot be switched on. */
const isLocked = (permission) => !permission.isGranted && !canGrantPermission(permission);

const filteredGroupedPermissions = computed(() => {
  const term = searchQuery.value.trim().toLowerCase();
  const filteredGroups = {};

  for (const groupName in groupedPermissions.value) {
    const matches = groupedPermissions.value[groupName].filter(permission => {
      if (!term) return true;
      const nameMatch = permission.name.toLowerCase().includes(term);
      const displayNameMatch = permission.displayName?.toLowerCase().includes(term);
      return nameMatch || displayNameMatch;
    });

    // Only include groups that have matching permissions
    if (matches.length > 0) {
      filteredGroups[groupName] = matches;
    }
  }
  return filteredGroups;
});

const visibleGroupNames = computed(() => Object.keys(filteredGroupedPermissions.value));

/* ── Group summaries ─────────────────────────────────────────── */

/** Every permission in a group — the area permission plus its children, search aside. */
const groupMembers = (groupName) => groupedPermissions.value[groupName] || [];

const groupTotal = (groupName) => groupMembers(groupName).length;

const groupGranted = (groupName) => groupMembers(groupName).filter(p => p.isGranted).length;

const groupState = (groupName) => {
  const granted = groupGranted(groupName);
  if (granted === 0) return 'off';
  if (granted === groupTotal(groupName)) return 'all';
  return 'partial';
};

const groupStateLabel = (groupName) => t(`users.permissions.state.${groupState(groupName)}`);

const getGroupDisplayName = (groupName) => {
  const parentPermission = getParentPermission(groupName);
  if (parentPermission) {
    return parentPermission.displayName || parentPermission.name;
  }
  return groupName === 'System' ? t('users.permissions.systemGroup') : groupName;
};

/** The area permission carries the group's own name in the header, so its row is labelled by role. */
const itemLabel = (permission) => {
  if (!permission.parentName) return t('users.permissions.groupAccess');
  return permission.displayName || permission.name;
};

const permissionTotal = computed(() => permissions.value.length);
const grantedTotal = computed(() => permissions.value.filter(p => p.isGranted).length);

const userDisplayName = computed(() => {
  const current = user.value;
  if (!current) return '';
  const fullName = [current.name, current.surname].filter(Boolean).join(' ');
  return current.displayUserName || fullName || current.userName || current.email || '';
});

const headTitle = computed(() => {
  if (!userDisplayName.value) return t('users.editPermissions');
  return `${t('users.permissions.title')} — ${userDisplayName.value}`;
});

const headSubtitle = computed(() =>
  t('users.permissions.grantedSummary', { granted: grantedTotal.value, total: permissionTotal.value })
);

/* ── Expansion ───────────────────────────────────────────────── */

/** A search opens every matching group: hidden matches would look like no matches at all. */
const isExpanded = (groupName) => !!searchQuery.value.trim() || !!expandedGroups.value[groupName];

const toggleGroup = (groupName) => {
  expandedGroups.value = { ...expandedGroups.value, [groupName]: !expandedGroups.value[groupName] };
};

const expandAllGroups = () => {
  const expanded = {};
  Object.keys(groupedPermissions.value).forEach(groupName => {
    expanded[groupName] = true;
  });
  expandedGroups.value = expanded;
};

const collapseAllGroups = () => {
  expandedGroups.value = {};
};

/* ── Data ────────────────────────────────────────────────────── */

const loadUserPermissions = async () => {
  if (!props.userId) {
    error.value = t('users.permissions.invalidUser');
    return;
  }

  loading.value = true;
  error.value = '';

  try {
    // Use super admin services when in super admin context
    if (props.isSuperAdmin) {
      const permissionsResponse = await superAdminService.getUserPermissions(props.userId);
      user.value = { id: props.userId }; // Minimal user object for super admin context
      permissions.value = permissionsResponse || [];
    } else {
      // Load user info and permissions in parallel for regular context
      const [userResponse, permissionsResponse] = await Promise.all([
        userService.getTenantUserById(props.userId),
        userPermissionService.getUserPermissions(props.userId)
      ]);

      user.value = userResponse;
      permissions.value = permissionsResponse || [];
    }

    // Load saved group order
    loadGroupOrder();

    // If no saved order exists, create initial order from current groups
    if (groupOrder.value.length === 0) {
      const groups = {};
      permissions.value.forEach(permission => {
        const groupName = permission.parentName || 'System';
        if (!groups[groupName]) {
          groups[groupName] = [];
        }
      });
      groupOrder.value = Object.keys(groups);
      saveGroupOrder();
    }

    // Open the first area so the dialog does not read as empty.
    const [firstGroup] = Object.keys(groupedPermissions.value);
    searchQuery.value = '';
    expandedGroups.value = firstGroup ? { [firstGroup]: true } : {};
  } catch (err) {
    console.error('Error loading user permissions:', err);
    error.value = t('users.permissions.loadFailed');
  } finally {
    loading.value = false;
  }
};

/* ── Toggling ────────────────────────────────────────────────── */

// Helper: Get the appropriate permission service
const getPermissionService = () => {
  return props.isSuperAdmin ? superAdminService : userPermissionService;
};

const notifySuccess = (message) => store.dispatch('notify', { message, type: 'success' });

// Helper: Check if permission grant is valid
const validatePermissionGrant = (permission) => {
  if (permission.isGranted && !canGrantPermission(permission)) {
    Swal.fire({
      title: t('common.actions.warning'),
      text: t('users.permissions.parentRequired'),
      icon: 'warning'
    });
    permission.isGranted = false;
    return false;
  }
  return true;
};

// Helper: Grant a permission
const grantPermission = async (permission) => {
  const service = getPermissionService();
  await service.grantPermission(props.userId, permission.name);

  notifySuccess(t('users.permissions.grantSuccess', { permission: permission.displayName || permission.name }));
};

// Helper: Get child permissions that are granted
const getGrantedChildPermissions = (permission) => {
  return permissions.value.filter(p =>
    p.parentName === permission.name && p.isGranted
  );
};

// Helper: Revoke child permissions
const revokeChildPermissions = async (childPermissions) => {
  const service = getPermissionService();

  for (const childPermission of childPermissions) {
    try {
      await service.revokePermission(props.userId, childPermission.name);
      childPermission.isGranted = false;
    } catch (err) {
      console.error('Error revoking child permission:', err);
    }
  }
};

// Helper: Confirm and revoke parent permission with children
const confirmAndRevokeParent = async (permission) => {
  const childPermissions = getGrantedChildPermissions(permission);

  if (childPermissions.length === 0) {
    return true;
  }

  const result = await Swal.fire({
    title: t('users.permissions.revokeParent'),
    text: t('users.permissions.revokeParentConfirm', { count: childPermissions.length }),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('common.actions.confirm'),
    cancelButtonText: t('common.actions.cancel')
  });

  if (!result.isConfirmed) {
    permission.isGranted = true;
    return false;
  }

  await revokeChildPermissions(childPermissions);
  return true;
};

// Helper: Revoke a permission
const revokePermission = async (permission) => {
  const service = getPermissionService();
  await service.revokePermission(props.userId, permission.name);

  notifySuccess(t('users.permissions.revokeSuccess', { permission: permission.displayName || permission.name }));
};

// Helper: Handle permission toggle error
const handlePermissionError = (err, permission) => {
  console.error('Error toggling permission:', err);
  permission.isGranted = !permission.isGranted;

  if (err?.response?.data?.error?.message) {
    emit('error', err);
  } else {
    Swal.fire({
      title: t('common.actions.error'),
      text: permission.isGranted
        ? t('users.permissions.revokeFailed')
        : t('users.permissions.grantFailed'),
      icon: 'error'
    });
  }
};

// Main function: Toggle permission
const togglePermission = async (permission) => {
  if (updating.value) return;

  if (!validatePermissionGrant(permission)) {
    return;
  }

  updating.value = true;

  try {
    if (permission.isGranted) {
      await grantPermission(permission);
    } else {
      const isParent = !permission.parentName;
      const shouldContinue = isParent
        ? await confirmAndRevokeParent(permission)
        : true;

      if (!shouldContinue) {
        updating.value = false;
        return;
      }

      await revokePermission(permission);
    }
  } catch (err) {
    handlePermissionError(err, permission);
  } finally {
    updating.value = false;
  }
};

/** The row is a button now, so it flips the model itself before the request goes out. */
const togglePermissionRow = (permission) => {
  if (updating.value || isLocked(permission)) return;
  permission.isGranted = !permission.isGranted;
  togglePermission(permission);
};

// Helper: Toggle a single group permission
const toggleSingleGroupPermission = async (permission, shouldSelect) => {
  if (permission.isGranted === shouldSelect) {
    return;
  }

  permission.isGranted = shouldSelect;
  const service = getPermissionService();

  if (shouldSelect) {
    await service.grantPermission(props.userId, permission.name);
  } else {
    await service.revokePermission(props.userId, permission.name);
  }
};

// Helper: Revert group permissions on error
const revertGroupPermissions = (members, shouldSelect) => {
  members.forEach(permission => {
    permission.isGranted = !shouldSelect;
  });
};

// Helper: Handle group toggle error
const handleGroupToggleError = (err, groupName, shouldSelect) => {
  console.error('Error toggling group permissions:', err);

  revertGroupPermissions(groupMembers(groupName), shouldSelect);

  if (err?.response?.data?.error?.message) {
    emit('error', err);
  } else {
    Swal.fire({
      title: t('common.actions.error'),
      text: t('users.permissions.groupToggleFailed'),
      icon: 'error'
    });
  }
};

/**
 * The one switch in a group header covers the area permission and its children together.
 * Granting runs parent first so the children are allowed; revoking runs the other way round.
 */
const toggleGroupAll = async (groupName) => {
  if (updating.value) return;

  const shouldSelect = groupState(groupName) !== 'all';
  const members = groupMembers(groupName);
  const sequence = shouldSelect ? members : [...members].reverse();

  updating.value = true;

  try {
    for (const permission of sequence) {
      await toggleSingleGroupPermission(permission, shouldSelect);
    }
    notifySuccess(
      shouldSelect
        ? t('users.permissions.groupGrantSuccess')
        : t('users.permissions.groupRevokeSuccess')
    );
  } catch (err) {
    handleGroupToggleError(err, groupName, shouldSelect);
  } finally {
    updating.value = false;
  }
};

/* ── Drag and drop group ordering ────────────────────────────── */

const draggedGroup = ref(null);
const draggedIndex = ref(null);

const onDragStart = (event, groupName) => {
  draggedGroup.value = groupName;
  draggedIndex.value = visibleGroupNames.value.indexOf(groupName);
  event.dataTransfer.effectAllowed = 'move';
  event.target.style.opacity = '0.5';
};

const onDragEnd = (event) => {
  event.target.style.opacity = '1';
  draggedGroup.value = null;
  draggedIndex.value = null;

  // Remove drag-over class from all elements
  document.querySelectorAll('.pm-group').forEach(el => {
    el.classList.remove('drag-over');
  });
};

const onDragOver = (event) => {
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
};

const onDragEnter = (event, targetGroupName) => {
  event.preventDefault();
  if (draggedGroup.value && draggedGroup.value !== targetGroupName) {
    event.currentTarget.classList.add('drag-over');
  }
};

const onDragLeave = (event) => {
  event.currentTarget.classList.remove('drag-over');
};

const onDrop = (event, targetGroupName) => {
  event.preventDefault();
  event.currentTarget.classList.remove('drag-over');

  if (draggedGroup.value && draggedGroup.value !== targetGroupName) {
    const currentGroups = visibleGroupNames.value;
    const targetIndex = currentGroups.indexOf(targetGroupName);
    const draggedGroupName = draggedGroup.value;

    // Remove dragged group from current position
    const newOrder = currentGroups.filter(name => name !== draggedGroupName);

    // Insert at new position
    newOrder.splice(targetIndex, 0, draggedGroupName);

    // Update group order
    groupOrder.value = newOrder;
    saveGroupOrder();
  }
};

// Watch for userId changes to reload permissions
watch(() => props.userId, (newUserId) => {
  if (newUserId && props.show) {
    loadUserPermissions();
  }
});

// Watch for show prop to load permissions when modal opens
watch(() => props.show, (isShown) => {
  if (isShown && props.userId) {
    loadUserPermissions();
  }
});

// Expose loadUserPermissions method for parent component
defineExpose({
  loadUserPermissions
});
</script>

<style scoped>
/* ── Dialog shell ────────────────────────────────────────────── */
.pm-dialog {
  max-width: 800px;
  width: 100%;
  margin: 42px auto;
}

.pm-content {
  border: 0;
  border-radius: 14px;
  box-shadow: 0 30px 70px rgba(8, 22, 45, 0.35);
  max-height: calc(100vh - 84px);
  overflow: hidden;
}

/* ── Head ────────────────────────────────────────────────────── */
.pm-head {
  padding: 15px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid var(--vp-gray-200);
  flex-wrap: wrap;
}

.pm-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--vp-heading-color);
  margin: 0;
  text-wrap: pretty;
}

.pm-subtitle {
  font-size: 0.78rem;
  color: var(--vp-gray-500);
  margin-top: 2px;
}

.pm-close {
  margin-left: auto;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  border: 1px solid var(--vp-gray-300);
  background: var(--vp-surface);
  color: var(--vp-muted-color);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.pm-close:hover {
  background: var(--vp-gray-200);
}

/* ── Toolbar ─────────────────────────────────────────────────── */
.pm-toolbar {
  padding: 13px 20px;
  display: flex;
  align-items: center;
  gap: 9px;
  flex-wrap: wrap;
  background: #fbfcfe;
  border-bottom: 1px solid var(--vp-gray-200);
}

.pm-search {
  display: flex;
  align-items: center;
  gap: 9px;
  border: 1px solid var(--vp-gray-300);
  border-radius: 10px;
  padding: 8px 12px;
  background: var(--vp-surface);
  flex: 1 1 200px;
  max-width: 320px;
  color: var(--vp-gray-500);
  margin: 0;
}

.pm-search input {
  border: 0;
  background: transparent;
  outline: none;
  font-size: 0.84rem;
  width: 100%;
  color: var(--vp-body-color);
}

.pm-tool-btn {
  border: 1px solid var(--vp-gray-300);
  background: var(--vp-surface);
  color: var(--vp-body-color);
  border-radius: 9px;
  padding: 8px 13px;
  font-size: 0.79rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  flex: 0 0 auto;
}

.pm-tool-btn:hover {
  background: var(--vp-gray-200);
}

.pm-tool-count {
  margin-left: auto;
  font-size: 0.78rem;
  color: var(--vp-gray-500);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* ── Body ────────────────────────────────────────────────────── */
.pm-body {
  overflow: auto;
  flex: 1;
  padding: 12px 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  min-height: 200px;
}

.pm-group {
  border: 1px solid var(--vp-gray-200);
  border-radius: 11px;
  overflow: hidden;
  min-width: 0;
  flex: 0 0 auto;
  cursor: grab;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.pm-group.is-on {
  border-color: rgba(var(--vp-accent-rgb), 0.35);
}

.pm-group:active {
  cursor: grabbing;
}

.pm-group.drag-over {
  border: 2px dashed var(--vp-accent);
  box-shadow: 0 4px 14px rgba(27, 46, 94, 0.12);
}

.pm-group-head {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 12px 14px;
  background: #fbfcfe;
  flex-wrap: wrap;
}

.pm-group.is-on .pm-group-head {
  background: rgba(var(--vp-accent-rgb), 0.05);
}

.pm-caret {
  width: 22px;
  height: 22px;
  border: 0;
  background: transparent;
  color: var(--vp-muted-color);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  padding: 0;
}

.pm-group-label {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--vp-heading-color);
  min-width: 0;
  flex: 1 1 180px;
  text-wrap: pretty;
}

.pm-group-count {
  font-size: 0.74rem;
  color: var(--vp-gray-500);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.pm-group-count.is-on {
  color: #0b7fa8;
}

.pm-group-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: transparent;
  padding: 0;
  white-space: nowrap;
  flex: 0 0 auto;
}

.pm-group-toggle:disabled {
  opacity: 0.6;
  cursor: default;
}

.pm-group-toggle-label {
  font-size: 0.79rem;
  font-weight: 600;
  color: var(--vp-body-color);
}

/* ── Switch ──────────────────────────────────────────────────── */
.pm-switch {
  width: 38px;
  height: 22px;
  border-radius: 11px;
  background: var(--vp-gray-400);
  position: relative;
  flex-shrink: 0;
  transition: background 0.15s;
}

.pm-switch.is-all {
  background: var(--vp-accent);
}

/* Half-lit: some of the area is on, so the track is too. */
.pm-switch.is-partial {
  background: rgba(var(--vp-accent-rgb), 0.45);
}

.pm-switch i {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(16, 42, 79, 0.3);
  transition: left 0.15s;
}

.pm-switch.is-all i {
  left: 19px;
}

/* Parked mid-track, which reads as "some of this area" without claiming on or off. */
.pm-switch.is-partial i {
  left: 11px;
}

/* ── Permission rows ─────────────────────────────────────────── */
.pm-items {
  display: flex;
  flex-direction: column;
}

.pm-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px 10px 44px;
  border: 0;
  border-top: 1px solid #f4f7fa;
  background: var(--vp-surface);
  text-align: left;
  width: 100%;
}

.pm-item:hover:not(:disabled) {
  background: #fbfdff;
}

.pm-item:disabled {
  cursor: default;
}

.pm-item.is-locked {
  opacity: 0.7;
}

.pm-item-text {
  min-width: 0;
  flex: 1;
}

.pm-item-label {
  display: block;
  font-size: 0.84rem;
  color: var(--vp-body-color);
  text-wrap: pretty;
}

.pm-item-hint {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  color: #8a6112;
  margin-top: 3px;
}

/* ── States ──────────────────────────────────────────────────── */
.pm-state {
  padding: 40px 20px;
  text-align: center;
  font-size: 0.86rem;
  color: var(--vp-gray-500);
}

.pm-state--error {
  color: #c1291a;
}

.pm-empty {
  padding: 46px 20px;
  text-align: center;
  color: var(--vp-gray-500);
}

.pm-empty > i {
  font-size: 2rem;
  color: var(--vp-gray-400);
}

.pm-empty-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--vp-muted-color);
  margin: 10px 0 4px;
}

.pm-empty-text {
  font-size: 0.82rem;
  margin: 0;
}

/* ── Foot ────────────────────────────────────────────────────── */
.pm-foot {
  padding: 13px 20px;
  border-top: 1px solid var(--vp-gray-200);
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.pm-foot-note {
  font-size: 0.77rem;
  color: var(--vp-gray-500);
}

.pm-btn {
  margin-left: auto;
  font-size: 0.84rem;
  border-radius: 10px;
  border: 1px solid var(--vp-gray-300);
  padding: 9px 16px;
  font-weight: 600;
  background: var(--vp-surface);
  color: var(--vp-body-color);
  white-space: nowrap;
  flex: 0 0 auto;
}

.pm-btn:hover {
  background: var(--vp-gray-200);
}

@media (max-width: 575.98px) {
  .pm-dialog {
    margin: 16px auto;
  }

  .pm-content {
    max-height: calc(100vh - 32px);
  }

  .pm-search {
    max-width: none;
  }

  .pm-item {
    padding-left: 24px;
  }
}
</style>
